import { APPLIED_PROPERTIES, appliedProperties, type AppliedPalette } from "@baton/tokens";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

/**
 * FR-015-130, FR-015-103, `contracts/design-system.md` § Token architecture and § Motion:
 * `data-theme`, `data-density` and `data-motion` on the document element. The system theme
 * leaves `data-theme` absent so the stylesheet resolves it through `prefers-color-scheme`,
 * and a switch rewrites custom properties only. Reduced motion is the single override in
 * the token stylesheet; no Baton-written component carries a reduced-motion branch of its own.
 *
 * 2026-09-15, by the owner's decision: the components taken from Spectrum UI animate through
 * Motion, which the token stylesheet cannot reach, so the same resolved setting is carried to
 * Motion — by `MotionBridge`, which reads `motionReduced` from this context. The bridge is a
 * module of its own and not part of this provider (2026-09-21, T292): the public entry mounts the
 * provider and no Motion component, and a provider that imported `motion/react` pulled the 41 KB
 * engine into the public chunk and put it 32.5 KB over its FR-015-120 budget. The authenticated
 * entry wraps its tree in the bridge, so `data-motion="reduced"` and the system preference stop
 * Motion there as they stop CSS.
 *
 * 024 FR-024-024 and FR-024-026 (T036): a workspace palette is applied the same way, by writing the
 * closed subset of `@baton/tokens`' `appliedProperties` (the accent, its on-solid, the focus ring and
 * the selected tint) as inline custom properties on the document element for the resolved theme, rewritten on a theme switch and removed when the palette is null.
 * No node is added, removed or replaced and no other element's `style` is touched. A host that knows
 * the palette when it mounts, a public page, passes it as the `palette` prop; the workspace shell,
 * which learns it from the workspace read below the provider, sets it through `setPalette`, and the
 * prop wins while it is given.
 */
export type ThemeSetting = "light" | "dark" | "system";
export type Density = "comfortable" | "compact";
export type MotionSetting = "full" | "reduced" | "system";

export interface ThemeState {
  readonly theme: ThemeSetting;
  readonly density: Density;
  readonly motion: MotionSetting;
}

export interface ThemeContextValue extends ThemeState {
  /** The theme in effect once `system` is resolved through the media query. */
  readonly resolvedTheme: "light" | "dark";
  readonly setTheme: (theme: ThemeSetting) => void;
  readonly setDensity: (density: Density) => void;
  readonly setMotion: (motion: MotionSetting) => void;
  /** light → dark → system → light, the order of the `Cmd+Shift+L` shortcut. */
  readonly cycleTheme: () => void;
  /** The palette in effect: the `palette` prop when given, else what `setPalette` last set. */
  readonly palette: AppliedPalette | null;
  /** Apply a workspace palette learned after mount, or null to return to the static accent. */
  readonly setPalette: (palette: AppliedPalette | null) => void;
  /** Whether motion is reduced once `system` is resolved through the media query: what `MotionBridge` carries to Motion. */
  readonly motionReduced: boolean;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export const defaultThemeState: ThemeState = { theme: "light", density: "comfortable", motion: "system" };

export interface ThemeProviderProps {
  readonly initial?: Partial<ThemeState>;
  /** Called on every change so the host persists it (local storage, `PATCH /me`). */
  readonly onChange?: (state: ThemeState) => void;
  /** A palette known at mount, applied over the static accent; null or absent leaves the static tokens. */
  readonly palette?: AppliedPalette | null;
  readonly children: ReactNode;
}

const DARK_QUERY = "(prefers-color-scheme: dark)";
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

function applyAttributes(state: ThemeState): void {
  const root = document.documentElement;
  if (state.theme === "system") root.removeAttribute("data-theme");
  else root.setAttribute("data-theme", state.theme);
  root.setAttribute("data-density", state.density);
  if (state.motion === "system") root.removeAttribute("data-motion");
  else root.setAttribute("data-motion", state.motion);
}

/** Write the palette's properties for `theme`, or clear them, on the document element only (FR-024-026). */
function applyPalette(palette: AppliedPalette | null, theme: "light" | "dark"): void {
  const style = document.documentElement.style;
  if (palette === null) {
    for (const name of APPLIED_PROPERTIES) style.removeProperty(name);
    return;
  }
  const properties = appliedProperties(palette, theme);
  for (const name of APPLIED_PROPERTIES) style.setProperty(name, properties[name]);
}

function readSystemDark(): boolean {
  return window.matchMedia(DARK_QUERY).matches;
}

function readSystemReducedMotion(): boolean {
  return window.matchMedia(REDUCED_MOTION_QUERY).matches;
}

/** Motion is reduced when the setting says so, or when it follows the system and the system asks for it. */
function motionReduced(motion: MotionSetting, systemReduced: boolean): boolean {
  return motion === "reduced" || (motion === "system" && systemReduced);
}

export function ThemeProvider({ initial, onChange, palette: paletteProp, children }: ThemeProviderProps) {
  const [state, setState] = useState<ThemeState>({ ...defaultThemeState, ...initial });
  const [systemDark, setSystemDark] = useState(readSystemDark);
  const [systemReduced, setSystemReduced] = useState(readSystemReducedMotion);
  const [setPaletteValue, setPaletteState] = useState<AppliedPalette | null>(null);
  const reduced = motionReduced(state.motion, systemReduced);
  const resolvedTheme = state.theme === "system" ? (systemDark ? "dark" : "light") : state.theme;
  const palette = paletteProp ?? setPaletteValue;

  useEffect(() => {
    applyAttributes(state);
    onChange?.(state);
  }, [state, onChange]);

  useEffect(() => {
    applyPalette(palette, resolvedTheme);
  }, [palette, resolvedTheme]);

  useEffect(() => {
    const query = window.matchMedia(DARK_QUERY);
    const listener = (event: MediaQueryListEvent) => {
      setSystemDark(event.matches);
    };
    query.addEventListener("change", listener);
    return () => {
      query.removeEventListener("change", listener);
    };
  }, []);

  useEffect(() => {
    const query = window.matchMedia(REDUCED_MOTION_QUERY);
    const listener = (event: MediaQueryListEvent) => {
      setSystemReduced(event.matches);
    };
    query.addEventListener("change", listener);
    return () => {
      query.removeEventListener("change", listener);
    };
  }, []);

  const setTheme = useCallback((theme: ThemeSetting) => {
    setState((s) => ({ ...s, theme }));
  }, []);
  const setDensity = useCallback((density: Density) => {
    setState((s) => ({ ...s, density }));
  }, []);
  const setMotion = useCallback((motion: MotionSetting) => {
    setState((s) => ({ ...s, motion }));
  }, []);
  const cycleTheme = useCallback(() => {
    setState((s) => ({ ...s, theme: s.theme === "light" ? "dark" : s.theme === "dark" ? "system" : "light" }));
  }, []);
  const setPalette = useCallback((next: AppliedPalette | null) => {
    setPaletteState(next);
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({
      ...state,
      resolvedTheme,
      setTheme,
      setDensity,
      setMotion,
      cycleTheme,
      palette,
      setPalette,
      motionReduced: reduced,
    }),
    [state, resolvedTheme, setTheme, setDensity, setMotion, cycleTheme, palette, setPalette, reduced],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext);
  if (value === null) throw new Error("useTheme must be used inside <ThemeProvider>");
  return value;
}
